/* eslint-disable-next-line no-unused-vars -- Node `tsx` prerender compiles JSX to `React.createElement` */
import React from 'react';
import { motion } from 'framer-motion';

const Logo = ({
  size = 40,
  showText = true,
  shortText = false,
  showTagline = false,
  textClass = '',
  variant = 'color',
  className = '',
  animated = false,
  /** Compatibility prop: when true, renders only the standalone fcp monogram. */
  withAppMark = false,
  appMarkSize,
}) => {
  const isWhite = variant === 'white';
  const isColor = variant === 'color';
  const textBase = isWhite ? '#FFFFFF' : variant === 'dark' ? '#F5F5F5' : '#0B0B0F';
  const orange = '#D5A01A';
  const taglineColor = isWhite ? 'rgba(255,255,255,0.68)' : '#666666';
  /** `color` variant: dark ink on light bg, stone-100 on dark bg (avoids invisible wordmark on login / dark UI). */
  /** Sidebar / light surfaces: force ink so parent text color never washes out "fuelcycle". */
  const fuelcycleClass = isColor ? '!text-stone-900 dark:!text-stone-100' : '';
  const wordmarkSize = Math.max(16, Math.round(size * 0.62));
  const tracking = '-0.01em';
  const wordmarkShellStyle = {
    display: 'inline-flex',
    alignItems: 'baseline',
    fontSize: wordmarkSize,
    fontFamily: 'var(--font-wordmark)',
    fontWeight: 700,
    letterSpacing: tracking,
    textTransform: 'lowercase',
  };

  const staticWordmark = (
    <span style={wordmarkShellStyle}>
      <span className={fuelcycleClass} style={isColor ? undefined : { color: textBase }}>
        fuelcycle
      </span>
      <span
        style={{
          color: orange,
          display: 'inline-block',
          lineHeight: 0.98,
        }}
      >
        pro
      </span>
    </span>
  );

  const animatedWordmark = (
    <span style={wordmarkShellStyle}>
      <motion.span
        className={fuelcycleClass}
        style={isColor ? undefined : { color: textBase }}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        fuelcycle
      </motion.span>
      <motion.span
        style={{
          color: orange,
          display: 'inline-block',
          lineHeight: 0.98,
        }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
      >
        pro
      </motion.span>
    </span>
  );

  const markPx = appMarkSize ?? Math.max(32, Math.round(size * 0.9));
  const renderWordmark = !withAppMark && showText;

  return (
    <span className={`inline-flex flex-col items-center ${className}`} aria-label="FuelCyclePro logo text" role="img">
      {withAppMark ? (
        <img
          src="/brand/fcp-mark.svg"
          alt=""
          width={markPx}
          height={markPx}
          className="mb-3 object-contain select-none"
          aria-hidden
        />
      ) : renderWordmark ? (
        <span className="flex flex-col items-center">
          <span
            className={`leading-none ${textClass} ${isColor ? fuelcycleClass : ''}`}
            style={{
              fontFamily: 'var(--font-display)',
              ...(isColor ? {} : { color: textBase }),
            }}
          >
            {shortText ? (
              <span
                className={isColor ? fuelcycleClass : undefined}
                style={{ fontWeight: 800, letterSpacing: tracking, ...(isColor ? {} : { color: textBase }) }}
              >
                FCP
              </span>
            ) : animated ? (
              animatedWordmark
            ) : (
              staticWordmark
            )}
          </span>
          {showTagline && (
            <span
              className={`text-[0.6rem] font-normal tracking-[0.2em] uppercase mt-2 leading-none ${
                isColor ? 'text-stone-500 dark:text-stone-400' : ''
              }`}
              style={{
                color: isColor ? undefined : taglineColor,
                fontFamily: 'var(--font-body)',
              }}
            >
              Know before you ride
            </span>
          )}
        </span>
      ) : null}
    </span>
  );
};

/** Inline wordmark matching header Logo — lowercase fuelcycle + orange pro */
export function BrandWordmark({ className = '', size = 'inherit' }) {
  const orange = '#D5A01A';
  const style =
    size === 'inherit'
      ? undefined
      : { fontSize: typeof size === 'number' ? size : undefined };
  return (
    <span
      className={`inline-flex items-baseline font-extrabold lowercase tracking-tight ${className}`}
      style={style}
      aria-label="fuelcyclepro"
    >
      <span className="text-stone-900 dark:text-stone-100">fuelcycle</span>
      <span style={{ color: orange }}>pro</span>
    </span>
  );
}

export default Logo;
